// Маленькое хранилище состояния вне React: читается и пишется синхронно из любого кода
// (обработчики, шина событий, перехват .value для внешних скриптов), компоненты подписываются хуком.
import { useSyncExternalStore } from 'react';

export interface Store<T> {
  get(): T;
  set(next: Partial<T> | ((prev: T) => Partial<T>)): void;
  replace(next: T): void;
  subscribe(fn: () => void): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const subs = new Set<() => void>();
  const notify = () => { for (const fn of [...subs]) fn(); };
  return {
    get: () => state,
    set(next) {
      const patch = typeof next === 'function' ? next(state) : next;
      state = { ...state, ...patch };
      notify();
    },
    replace(next) {
      state = next;
      notify();
    },
    subscribe(fn) {
      subs.add(fn);
      return () => { subs.delete(fn); };
    },
  };
}

/** Подписка компонента на хранилище (или его часть) */
export function useStore<T extends object>(s: Store<T>): T;
export function useStore<T extends object, R>(s: Store<T>, select: (state: T) => R): R;
export function useStore<T extends object, R>(s: Store<T>, select?: (state: T) => R): T | R {
  const get = () => (select ? select(s.get()) : s.get());
  return useSyncExternalStore(s.subscribe, get, get);
}
